import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { LogoIcon } from "@/components/Logo";
import Footer from "@/components/Footer";
import {
  BookOpen, MessageCircle, Brain, ArrowRight, Sparkles,
  Camera, CheckCircle, Zap, FileText, Clock, Send,
  ChevronRight, Users, Upload
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const goTo = user ? "/home" : "/auth?mode=signup";
  const [activeDemo, setActiveDemo] = useState("explain");

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-x-hidden" data-testid="landing-page">

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0F1B3D]/90 border-b border-white/10" data-testid="landing-nav">
        <div className="max-w-6xl mx-auto px-4 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoIcon className="w-8 h-8 sm:w-10 sm:h-10" />
            <span className="font-bold text-base sm:text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <span className="text-orange-400">Ai Studdy</span> <span className="text-blue-400">Buddy</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate(user ? "/home" : "/auth")}
              className="text-sm text-white/70 hover:text-white px-3 h-9 rounded-full">
              Log In
            </Button>
            <Button data-testid="nav-signup-btn" onClick={() => navigate(goTo)}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 h-9 rounded-full font-semibold shadow-sm">
              Sign Up Free
            </Button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="pt-16 relative overflow-hidden" data-testid="hero-section">
        <div className="bg-[#0F1B3D] px-4 pt-10 sm:pt-16 pb-28 sm:pb-32 relative">
          <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-orange-500/10 rounded-full blur-[80px]" />
          <div className="max-w-3xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/10 text-orange-400 rounded-full px-3 py-1 text-xs font-bold mb-4 border border-white/10">
              <Sparkles className="w-3 h-3" /> AI-POWERED LEARNING
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.1] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Your AI Study Partner<br className="hidden sm:block" /> for <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-500">Class 5-12</span>
            </h1>
            <p className="text-white/60 text-sm sm:text-base mb-6 max-w-md mx-auto">
              CBSE & ICSE board exam prep with AI explanations, doubt solving, tests & homework solver. <span className="text-white font-semibold">Free for students.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 justify-center mb-4">
              <Button data-testid="hero-get-started-btn" onClick={() => navigate(goTo)}
                className="rounded-full font-bold text-sm px-7 py-5 bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/30 active:scale-95 transition-all">
                Start Learning Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-demo-btn" onClick={() => document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="rounded-full font-bold text-sm px-7 py-5 bg-white/10 hover:bg-white/20 text-white border border-white/20 active:scale-95 transition-all">
                See How It Works
              </Button>
            </div>
            <div className="flex items-center justify-center gap-3 text-[11px] text-white/40">
              <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-400" /> Easy Explanation</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-400" /> Ask Doubts</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-400" /> NCERT & CISCE Syllabus</span>
            </div>
          </div>
        </div>

        {/* Floating cards */}
        <div className="max-w-4xl mx-auto px-4 -mt-20 sm:-mt-24 relative z-10 mb-6">
          <div className="flex justify-center gap-3 sm:gap-5">
            <div className="w-[48%] sm:w-[260px] bg-white rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.1)] border border-slate-200 p-3 sm:p-4 -rotate-2">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center"><BookOpen className="w-3 h-3 text-blue-600" /></div>
                <p className="text-xs sm:text-xs font-bold text-[#0F1B3D]">Refraction of Light</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-2 border border-blue-100 mb-1.5">
                <p className="text-[11px] font-bold text-[#0F1B3D]">Snell's Law</p>
                <p className="text-[11px] text-slate-600 font-mono">n₁ sin θ₁ = n₂ sin θ₂</p>
              </div>
              <div className="flex items-center gap-1 text-orange-500">
                <Sparkles className="w-2.5 h-2.5" /><p className="text-xs font-bold">AI Explained</p>
              </div>
            </div>
            <div className="w-[48%] sm:w-[260px] bg-white rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.1)] border border-slate-200 p-3 sm:p-4 rotate-2 mt-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-green-100 flex items-center justify-center"><MessageCircle className="w-3 h-3 text-green-600" /></div>
                <p className="text-xs sm:text-xs font-bold text-[#0F1B3D]">Doubt Chat</p>
              </div>
              <div className="flex justify-end mb-1"><div className="bg-blue-600 text-white rounded-xl px-2 py-1 text-xs">What is Newton's Third Law?</div></div>
              <div className="bg-slate-100 rounded-xl px-2 py-1.5 text-xs text-slate-700">
                <p className="font-bold text-[#0F1B3D]">Newton's Third Law</p>
                <p>"Every action has an equal and opposite reaction"</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {["Maths", "Physics", "Chemistry", "Biology", "English", "Hindi"].map((s, i) => {
              const c = ["bg-blue-50 text-blue-700 border-blue-200", "bg-indigo-50 text-indigo-700 border-indigo-200", "bg-green-50 text-green-700 border-green-200", "bg-emerald-50 text-emerald-700 border-emerald-200", "bg-purple-50 text-purple-700 border-purple-200", "bg-orange-50 text-orange-700 border-orange-200"];
              return <span key={s} className={`${c[i]} text-xs sm:text-xs font-semibold px-3 py-1 rounded-full border`}>{s}</span>;
            })}
          </div>
        </div>
      </section>

      {/* ── Trust Banner ────────────────────────────────────────────── */}
      <section className="py-4 bg-[#0F1B3D]">
        <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-white/80 text-xs sm:text-sm font-medium px-4">
          {["CBSE & ICSE", "Class 5 to 12", "700+ Chapters", "AI Tutor 24/7"].map(v => (
            <span key={v} className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-orange-400" /> {v}</span>
          ))}
        </div>
      </section>

      {/* ── Features (compact) ──────────────────────────────────────── */}
      <section className="py-12 sm:py-16 px-4 bg-white" data-testid="features-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-black text-[#0F1B3D] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Everything You Need to Score Big
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[
              { icon: BookOpen, title: "AI Explanations", desc: "Every chapter explained by AI", bg: "bg-blue-50", ic: "text-blue-600" },
              { icon: MessageCircle, title: "24/7 Doubt Chat", desc: "Ask any question, anytime", bg: "bg-green-50", ic: "text-green-600" },
              { icon: Brain, title: "Board Exam Tests", desc: "CBSE & ICSE exam patterns", bg: "bg-purple-50", ic: "text-purple-600" },
              { icon: Camera, title: "Photo Solver", desc: "Snap homework, get solutions", bg: "bg-orange-50", ic: "text-orange-600" },
              { icon: FileText, title: "Practice MCQs", desc: "Topic-wise questions", bg: "bg-rose-50", ic: "text-rose-600" },
              { icon: Clock, title: "Exam Countdown", desc: "Track your preparation", bg: "bg-amber-50", ic: "text-amber-600" },
            ].map(f => (
              <div key={f.title} className={`${f.bg} rounded-xl p-4 border border-slate-100`}>
                <f.icon className={`w-6 h-6 ${f.ic} mb-2`} strokeWidth={2} />
                <h3 className="font-bold text-[#0F1B3D] text-sm mb-0.5">{f.title}</h3>
                <p className="text-slate-500 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Interactive Demo ──────────────────────────────────────── */}
      <section id="demo-section" className="py-12 sm:py-16 px-4" data-testid="demo-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-orange-600 text-xs font-bold tracking-[0.2em] uppercase mb-2">TRY IT YOURSELF</p>
            <h2 className="text-2xl sm:text-3xl font-black text-[#0F1B3D] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              See How It Works
            </h2>
          </div>
          <div className="grid lg:grid-cols-5 gap-4 items-start">
            <div className="lg:col-span-2 flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0">
              {[
                { id: "explain", label: "AI Explanations", icon: BookOpen, ac: "bg-blue-600 text-white" },
                { id: "homework", label: "Photo Solver", icon: Camera, ac: "bg-orange-500 text-white" },
                { id: "doubt", label: "Doubt Chat", icon: MessageCircle, ac: "bg-green-600 text-white" },
                { id: "test", label: "Board Tests", icon: Brain, ac: "bg-purple-600 text-white" },
              ].map(tab => (
                <button key={tab.id} data-testid={`demo-tab-${tab.id}`} onClick={() => setActiveDemo(tab.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all whitespace-nowrap flex-shrink-0 lg:w-full ${activeDemo === tab.id ? `${tab.ac} border-transparent shadow-lg` : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  <tab.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-semibold text-sm">{tab.label}</span>
                  {activeDemo === tab.id && <ChevronRight className="w-4 h-4 ml-auto hidden lg:block" />}
                </button>
              ))}
            </div>
            <div className="lg:col-span-3">
              {activeDemo === "explain" && (
                <div className="rounded-2xl border p-5 bg-blue-50 border-blue-200">
                  <p className="font-bold text-[#0F1B3D] text-base mb-3">Photosynthesis — Class 10 Biology</p>
                  <div className="bg-white rounded-xl p-3 border border-blue-100 mb-2">
                    <p className="font-bold text-[#0F1B3D] text-xs mb-1">What is Photosynthesis?</p>
                    <p className="text-slate-600 text-xs">The process by which green plants convert light energy into chemical energy using CO₂ and water.</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-green-100 mb-2">
                    <p className="font-bold text-[#0F1B3D] text-xs mb-1">Chemical Equation</p>
                    <p className="text-xs font-mono bg-slate-50 rounded-lg p-2">6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-amber-100">
                    <p className="font-bold text-[#0F1B3D] text-xs mb-1">Key Points</p>
                    <ul className="text-slate-600 text-xs space-y-0.5"><li>• Occurs in chloroplasts</li><li>• Light reaction → Thylakoid membrane</li><li>• Dark reaction → Stroma (Calvin cycle)</li></ul>
                  </div>
                </div>
              )}
              {activeDemo === "homework" && (
                <div className="rounded-2xl border p-5 bg-orange-50 border-orange-200">
                  <p className="font-bold text-[#0F1B3D] text-base mb-3">Solve: Find x if 2x + 5 = 15</p>
                  <div className="bg-white rounded-xl p-3 border border-slate-200 flex items-center gap-3 mb-2">
                    <Upload className="w-6 h-6 text-orange-400" /><div><p className="font-bold text-[#0F1B3D] text-xs">homework_page3.jpg</p><p className="text-slate-400 text-xs">Uploaded</p></div>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-orange-100">
                    <p className="font-bold text-[#0F1B3D] text-xs mb-2">Step-by-Step Solution</p>
                    <div className="space-y-1 text-xs text-slate-700">
                      <p><span className="font-semibold text-orange-600">Step 1:</span> 2x + 5 = 15</p>
                      <p><span className="font-semibold text-orange-600">Step 2:</span> 2x = 15 - 5 = 10</p>
                      <p><span className="font-semibold text-orange-600">Step 3:</span> x = 10 ÷ 2 = <span className="font-bold text-green-600">5</span></p>
                    </div>
                  </div>
                </div>
              )}
              {activeDemo === "doubt" && (
                <div className="rounded-2xl border p-5 bg-green-50 border-green-200">
                  <p className="font-bold text-[#0F1B3D] text-base mb-3">Ask any doubt, get instant answers</p>
                  <div className="flex justify-end mb-2"><div className="bg-blue-600 text-white rounded-2xl rounded-br-md px-3 py-2 text-xs max-w-[80%]">What is Newton's Third Law? Give me an example</div></div>
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3 py-2 text-xs text-slate-700 mb-2">
                    <p className="font-bold text-[#0F1B3D] mb-1">Newton's Third Law</p>
                    <p>"For every action, there is an equal and opposite reaction."</p>
                    <p className="mt-1 text-slate-500"><span className="font-semibold text-blue-600">Example:</span> When you push a wall, the wall pushes you back!</p>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-xl p-2 border border-slate-200">
                    <input className="flex-1 bg-transparent text-xs outline-none px-2 text-slate-400" placeholder="Ask your doubt..." disabled />
                    <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center"><Send className="w-3 h-3 text-white" /></div>
                  </div>
                </div>
              )}
              {activeDemo === "test" && (
                <div className="rounded-2xl border p-5 bg-purple-50 border-purple-200">
                  <p className="font-bold text-[#0F1B3D] text-base mb-3">CBSE Class 10 Science — Board Pattern</p>
                  <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-purple-100 mb-2">
                    <div><p className="font-bold text-[#0F1B3D] text-xs">Total Marks: 80</p><p className="text-slate-500 text-xs">20 MCQ + 10 Short + 6 Long</p></div>
                    <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">Board Pattern</span>
                  </div>
                  <div className="bg-white rounded-xl p-3 border border-slate-200">
                    <p className="text-xs text-purple-600 font-bold mb-1">Q1. (1 mark)</p>
                    <p className="text-xs text-[#0F1B3D] font-medium mb-2">Which gas is evolved when zinc reacts with dilute sulphuric acid?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {["A. Oxygen", "B. Hydrogen", "C. Nitrogen", "D. Carbon dioxide"].map((o, i) => (
                        <div key={o} className={`text-xs px-3 py-1.5 rounded-lg border ${i === 1 ? 'bg-green-50 border-green-300 text-green-700 font-bold' : 'bg-white border-slate-200 text-slate-600'}`}>{o}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Curriculum ─────────────────────────────────────────────── */}
      <section className="py-12 sm:py-16 px-4 bg-white" data-testid="curriculum-section">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-orange-600 text-xs font-bold tracking-[0.2em] uppercase mb-2">COMPLETE COVERAGE</p>
            <h2 className="text-2xl sm:text-3xl font-black text-[#0F1B3D] tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Your Entire Syllabus, Covered
            </h2>
            <p className="text-slate-500 text-sm mb-5">CBSE or ICSE, Class 5 or 12 — every chapter aligned with your textbook.</p>
            {["CBSE: NCERT chapters across all subjects", "ICSE: Selina Concise, Treasure Trove aligned", "Grades 11-12: Science & Commerce streams", "Tests match your board's exam pattern"].map((item, i) => (
              <div key={i} className="flex items-start gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <p className="text-slate-600 text-sm">{item}</p>
              </div>
            ))}
          </div>
          <div>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { name: "Mathematics", icon: "📐", c: "bg-blue-50 text-blue-700 border-blue-200" },
                { name: "Physics", icon: "⚡", c: "bg-indigo-50 text-indigo-700 border-indigo-200" },
                { name: "Chemistry", icon: "🧪", c: "bg-green-50 text-green-700 border-green-200" },
                { name: "Biology", icon: "🌿", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                { name: "English", icon: "📖", c: "bg-purple-50 text-purple-700 border-purple-200" },
                { name: "Hindi", icon: "📝", c: "bg-orange-50 text-orange-700 border-orange-200" },
              ].map(s => (
                <div key={s.name} className={`${s.c} rounded-xl p-3 border flex items-center gap-2`}>
                  <span className="text-lg">{s.icon}</span><span className="font-bold text-xs">{s.name}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2.5 mt-2.5">
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-3 text-white">
                <p className="text-xs font-bold text-blue-200">CBSE</p>
                <p className="font-black text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>476+</p>
                <p className="text-blue-200 text-xs">Chapters</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl p-3 text-white">
                <p className="text-xs font-bold text-orange-200">ICSE</p>
                <p className="font-black text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>400+</p>
                <p className="text-orange-200 text-xs">Chapters</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────── */}
      <section className="py-12 sm:py-16 px-4 bg-[#0F1B3D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Start Learning in 30 Seconds
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "1", title: "Sign Up Free", desc: "Enter email, pick board and grade", icon: Users, color: "bg-blue-500" },
              { step: "2", title: "Pick Any Topic", desc: "Browse chapters from your textbook", icon: BookOpen, color: "bg-orange-500" },
              { step: "3", title: "Learn & Practice", desc: "AI explanations, doubts, tests", icon: CheckCircle, color: "bg-green-500" },
            ].map(item => (
              <div key={item.step}>
                <div className={`w-14 h-14 rounded-xl ${item.color} flex items-center justify-center mx-auto mb-3 shadow-lg`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-white text-base mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
            </div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Ready to Ace Your Exams?
              </h2>
              <p className="text-white/80 text-sm mb-6">Free for all students. Start learning now.</p>
              <Button data-testid="cta-get-started-btn" onClick={() => navigate(goTo)}
                className="rounded-full font-bold text-sm px-8 py-5 bg-white text-orange-600 hover:bg-orange-50 shadow-xl active:scale-95 transition-all">
                Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
